import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  Fragment,
} from "react";
import { useNavigate } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import { useAccount } from "wagmi";
import { useAppKit } from "@reown/appkit/react";
import ScrollReveal from "@/components/ScrollReveal";
import PullToRefresh from "@/components/PullToRefresh";
import { toast } from "sonner";
import {
  TrendingUp,
  TrendingDown,
  Zap,
  RefreshCw,
  AlertTriangle,
  Bot,
  Hand,
  ChevronRight,
} from "lucide-react";
import {
  MOCK_ORDERS,
  MOCK_ASSETS,
  AI_TRADING_CONFIG,
  formatUSDT,
} from "@/lib/mockData";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { Slider } from "@/components/ui/slider";
import {
  useMaxQuantKline,
  type MaxQuantInterval,
} from "@/hooks/useMaxQuantKline";
import SignalChart from "@/components/aiTrading/SignalChart";
import StrategyWizardDialog from "@/components/aiTrading/StrategyWizardDialog";
import OrdersHistorySection from "@/components/aiTrading/OrdersHistorySection";
import { cn, formatEtherWithDecimals, formatThousands } from "@/lib/utils";
import { useGetUserCexPnlSummary } from "@/hooks/api/useGetUserCexPnlSummary";
import { useGetPlatformCexSymbolDayStats } from "@/hooks/api/useGetPlatformCexSymbolDayStats";
import { useBalanceV2 } from "@/hooks/api/useBalanceV2";
import { USDT_ADDRESS } from "@/config/env";
import { getTokenName } from "@/config/common";
import { getToken } from "@/lib/auth";
import { useTranslation } from "react-i18next";
import { usePlatFormConfig } from "@/hooks/api/usePlatFormConfig";
import { showError } from "@/lib/toast";

// ===== Design: Dark trading terminal matching H5 reference =====
const C = {
  bg: "rgba(10,11,14,0.82)",
  card: "rgba(15,17,23,0.75)",
  card2: "rgba(22,26,34,0.72)",
  border: "rgba(255,255,255,0.07)",
  text: "#e2e8f0",
  muted: "#94a3b8",
  dim: "#64748b",
  primary: "#22c55e",
  green: "#22c55e",
  red: "#ef4444",
  amber: "#f59e0b",
  blue: "#3b82f6",
  cyan: "#06b6d4",
};

/**
 * H5 居中含宽：与 Home、Profile 各 `Dialog`（如 BuyAgent、Withdraw、BuyFUSD）一致；
 * 覆盖 `components/ui/dialog` 里默认的 `sm:max-w-lg`，避免大屏上过宽。
 */
const DIALOG_H5 = "max-w-sm w-full mx-auto sm:max-w-sm";

/* ─── Trading pairs ─── */
const TRADING_PAIRS = [
  {
    id: "BTC",
    name: "Bitcoin",
    symbol: "BTC",
    icon: "https://cryptologos.cc/logos/bitcoin-btc-logo.svg",
    color: "#f7931a",
    bgColor: "rgba(247,147,26,0.12)",
    borderColor: "rgba(247,147,26,0.25)",
  },
  {
    id: "ETH",
    name: "Ethereum",
    symbol: "ETH",
    icon: "https://cryptologos.cc/logos/ethereum-eth-logo.svg",
    color: "#627eea",
    bgColor: "rgba(98,126,234,0.12)",
    borderColor: "rgba(98,126,234,0.25)",
  },
  {
    id: "BNB",
    name: "BNB Chain",
    symbol: "BNB",
    icon: "https://cryptologos.cc/logos/bnb-bnb-logo.svg",
    color: "#f0b90b",
    bgColor: "rgba(240,185,11,0.12)",
    borderColor: "rgba(240,185,11,0.25)",
  },
  {
    id: "SOL",
    name: "Solana",
    symbol: "SOL",
    icon: "https://cryptologos.cc/logos/solana-sol-logo.svg",
    color: "#9945ff",
    bgColor: "rgba(153,69,255,0.12)",
    borderColor: "rgba(153,69,255,0.25)",
  },
];

const COIN_LOGOS: Record<string, string> = {
  BTC: "https://d2xsxph8kpxj0f.cloudfront.net/310519663463590886/GJdBY8WukCndJUnrRzN6pD/btc-logo_f2a40d34.png",
  ETH: "https://d2xsxph8kpxj0f.cloudfront.net/310519663463590886/GJdBY8WukCndJUnrRzN6pD/eth-logo-diamond_0995aa96.png",
  BNB: "https://d2xsxph8kpxj0f.cloudfront.net/310519663463590886/GJdBY8WukCndJUnrRzN6pD/bnb-logo-circle_a70b583b.png",
  SOL: "https://assets.coingecko.com/coins/images/4128/large/solana.png",
};

const SIGNAL_COINS = ["BTC", "ETH", "BNB", "SOL"];

/* ─── Helper Components ─── */
function DirBadge({ dir }: { dir: string }) {
  const { t } = useTranslation();
  const isLong = dir === "多";
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold mono-num"
      style={{
        background: isLong ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
        color: isLong ? C.green : C.red,
        border: `1px solid ${isLong ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.25)"}`,
      }}
    >
      {isLong ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
      {isLong ? t("aiTradingPage.badgeLong") : t("aiTradingPage.badgeShort")}
    </span>
  );
}

function SourceBadge({ source }: { source: string }) {
  const { t } = useTranslation();
  const isAuto = source === "auto";
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium"
      style={{
        background: isAuto ? "rgba(34,197,94,0.10)" : "rgba(59,130,246,0.10)",
        color: isAuto ? C.green : C.blue,
        border: `1px solid ${isAuto ? "rgba(34,197,94,0.20)" : "rgba(59,130,246,0.20)"}`,
      }}
    >
      {isAuto ? <Bot size={9} /> : <Hand size={9} />}
      {isAuto ? t("aiTradingPage.badgeAuto") : t("aiTradingPage.badgeManual")}
    </span>
  );
}

function PnlDisplay({
  value,
  size = "sm",
}: {
  value: number;
  size?: "sm" | "lg";
}) {
  const isPos = value >= 0;
  const textSize = size === "lg" ? "text-lg" : "text-sm";
  return (
    <span
      className={`mono-num font-bold ${textSize}`}
      style={{ color: isPos ? C.green : C.red }}
    >
      {isPos ? "+" : "-"}${formatUSDT(Math.abs(value))}
    </span>
  );
}

/* ─── AnimatedPnlRow: 今日/累计盈利数字从0滚动到目标值 ─── */
function AnimatedPnlRow({
  balanceUsdt,
  todayPnl,
  totalPnl,
  refreshKey = 0,
}: {
  balanceUsdt: number;
  todayPnl: number;
  totalPnl: number;
  refreshKey?: number;
}) {
  const { t } = useTranslation();
  const [todayDisplay, setTodayDisplay] = useState(0);
  const [totalDisplay, setTotalDisplay] = useState(0);
  const [balanceGlow, setBalanceGlow] = useState(false);

  useEffect(() => {
    setTodayDisplay(0);
    setTotalDisplay(0);
    setBalanceGlow(false);
    const duration = 1400;
    const startTime = performance.now();
    let raf: number;
    // easeOutBack with overshoot for bouncy feel
    const easeOutBack = (t: number): number => {
      const c1 = 1.70158;
      const c3 = c1 + 1;
      return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    };
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress === 1 ? 1 : easeOutBack(progress);
      setTodayDisplay(eased * todayPnl);
      setTotalDisplay(eased * totalPnl);
      if (progress < 1) {
        raf = requestAnimationFrame(animate);
      } else {
        // 动画完成后余额短暂光晕闪烁
        setBalanceGlow(true);
        setTimeout(() => setBalanceGlow(false), 600);
      }
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [refreshKey, todayPnl, totalPnl]);

  const todayColor = todayPnl >= 0 ? C.green : C.red;
  const totalColor = totalPnl >= 0 ? C.green : C.red;
  const todaySign = todayDisplay >= 0 ? "+" : "-";
  const totalSign = totalDisplay >= 0 ? "+" : "-";

  return (
    <>
      <div className="flex items-baseline gap-3 mb-2">
        <span
          className="text-[32px] font-extrabold mono-num tracking-tight leading-none transition-all duration-500"
          style={{
            color: C.text,
            textShadow: balanceGlow
              ? `0 0 12px ${C.green}, 0 0 24px rgba(34,197,94,0.3)`
              : "none",
            filter: balanceGlow ? "brightness(1.2)" : "brightness(1)",
          }}
        >
          ${formatThousands(balanceUsdt.toFixed(2))}
        </span>
        <span
          className="mono-num text-sm font-semibold"
          style={{ color: todayColor }}
        >
          {t("aiTradingPage.today")} {todaySign}$
          {formatThousands(Math.abs(todayDisplay).toFixed(2))}
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[10px]" style={{ color: C.dim }}>
          {t("aiTradingPage.cumulativePnl")}
        </span>
        <span
          className="mono-num text-xs font-semibold"
          style={{ color: totalColor }}
        >
          {totalSign}${formatThousands(Math.abs(totalDisplay).toFixed(2))}
        </span>
      </div>
    </>
  );
}

/* ─── Auto Trade Dialog (matching reference: signal + toggle + settings) ─── */
function AutoTradeDialog({
  open,
  onClose,
  autoEnabled,
  setAutoEnabled,
  margin,
  setMargin,
  leverage,
  setLeverage,
}: {
  open: boolean;
  onClose: () => void;
  autoEnabled: boolean;
  setAutoEnabled: (v: boolean) => void;
  margin: number;
  setMargin: (v: number) => void;
  leverage: number;
  setLeverage: (v: number) => void;
}) {
  const { t } = useTranslation();
  const tokenNameUsdt = getTokenName(USDT_ADDRESS);
  const [autoOpen, setAutoOpen] = useState(autoEnabled);
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setAutoEnabled(autoOpen);
      setSaving(false);
      if (autoOpen) toast.success(t("aiTradingPage.toastAutoOn"));
      else toast.info(t("aiTradingPage.toastAutoOff"));
      onClose();
    }, 600);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className={cn(DIALOG_H5, "max-h-[85vh] overflow-y-auto")}
        style={{
          background: "#0d0f14",
          border: "1px solid rgba(34,197,94,0.20)",
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle>{t("aiTradingPage.autoAiOrderTitle")}</DialogTitle>
          <p className="text-[11px] mt-0.5" style={{ color: C.dim }}>
            {t("aiTradingPage.autoAiOrderSubtitle")}
          </p>
        </DialogHeader>

        <div className="space-y-3">
          {/* AI Account Balance */}
          <div
            className="rounded-xl p-3 flex items-center justify-between"
            style={{ background: C.card2, border: `1px solid ${C.border}` }}
          >
            <span className="text-xs" style={{ color: C.muted }}>
              {t("aiTradingPage.aiAccountBalance")}
            </span>
            <span
              className="text-base font-bold mono-num"
              style={{ color: C.primary }}
            >
              ${formatUSDT(MOCK_ASSETS.usdt.available)}
            </span>
          </div>

          {/* AI Signal Status */}
          <div
            className="rounded-xl p-3"
            style={{
              background: "rgba(6,182,212,0.06)",
              border: "1px solid rgba(6,182,212,0.18)",
            }}
          >
            <div className="flex items-center gap-2.5">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: "rgba(6,182,212,0.15)" }}
              >
                <Zap size={16} style={{ color: C.cyan }} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className="text-xs font-semibold"
                    style={{ color: C.text }}
                  >
                    {t("aiTradingPage.aiSignalPush")}
                  </span>
                  <span
                    className="text-[9px] px-1.5 py-0.5 rounded-full font-medium"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(34,197,94,0.18) 0%, rgba(34,197,94,0.08) 100%)",
                      WebkitBackdropFilter: "blur(6px)",
                      backdropFilter: "blur(6px)",
                      color: C.green,
                      border: "1px solid rgba(34,197,94,0.20)",
                      boxShadow: "0 0 6px rgba(34,197,94,0.10)",
                    }}
                  >
                    {t("aiTradingPage.activeStatus")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Auto Open Toggle */}
          <div
            className="rounded-xl p-3 flex items-center justify-between"
            style={{ background: C.card2, border: `1px solid ${C.border}` }}
          >
            <div>
              <div className="text-xs font-semibold" style={{ color: C.text }}>
                {t("aiTradingPage.autoOpenTitle")}
              </div>
              <div className="text-[10px]" style={{ color: C.dim }}>
                {t("aiTradingPage.autoOpenDesc")}
              </div>
            </div>
            <button
              onClick={() => setAutoOpen(!autoOpen)}
              className="w-11 h-6 rounded-full transition-all relative"
              style={{
                background: autoOpen
                  ? "linear-gradient(180deg, rgba(34,197,94,0.5) 0%, rgba(34,197,94,0.3) 100%)"
                  : "rgba(255,255,255,0.10)",
                boxShadow: autoOpen
                  ? "0 0 12px rgba(34,197,94,0.20), inset 0 1px 0 rgba(255,255,255,0.15)"
                  : "none",
                border: autoOpen
                  ? "1px solid rgba(34,197,94,0.35)"
                  : "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <div
                className="w-5 h-5 rounded-full absolute top-0.5 transition-all"
                style={{
                  left: autoOpen ? "22px" : "2px",
                  background: autoOpen ? "#e8ffe8" : "#fff",
                  boxShadow: autoOpen
                    ? "0 0 6px rgba(34,197,94,0.4)"
                    : "0 1px 2px rgba(0,0,0,0.2)",
                }}
              />
            </button>
          </div>

          {/* Order Settings */}
          <div
            className="rounded-xl p-3 space-y-3"
            style={{ background: C.card2, border: `1px solid ${C.border}` }}
          >
            <div className="text-xs font-semibold" style={{ color: C.text }}>
              {t("aiTradingPage.autoOrderSettings")}
            </div>
            <div>
              <div className="flex justify-between text-[11px] mb-2">
                <span style={{ color: C.muted }}>
                  {t("aiTradingPage.perOrderUsdLabel")}
                </span>
                <span
                  className="mono-num font-bold text-sm"
                  style={{
                    color:
                      margin <= 500
                        ? C.green
                        : margin <= 1200
                          ? C.amber
                          : C.red,
                  }}
                >
                  {t("aiTradingPage.marginWithToken", {
                    amount: margin,
                    tokenNameUsdt,
                  })}
                </span>
              </div>
              {/* Slider: 100-2000, step 100, gradient track */}
              <div className="px-1 pt-1 pb-3">
                <Slider
                  value={[margin]}
                  onValueChange={v => setMargin(v[0])}
                  min={100}
                  max={2000}
                  step={100}
                  className="w-full [&_[role=slider]]:h-5 [&_[role=slider]]:w-5"
                />
              </div>
              {/* Tick marks */}
              <div className="flex justify-between px-1">
                {[100, 500, 1000, 1500, 2000].map(v => (
                  <button
                    key={v}
                    onClick={() => setMargin(v)}
                    className="text-[9px] mono-num transition-colors"
                    style={{
                      color:
                        margin === v
                          ? v <= 500
                            ? C.green
                            : v <= 1200
                              ? C.amber
                              : C.red
                          : C.dim,
                    }}
                  >
                    {v}
                  </button>
                ))}
              </div>
              {/* Risk warning when margin > 1000 */}
              {margin > 1000 && (
                <div
                  className="mt-2.5 px-2 py-2 rounded-lg flex items-start gap-2 transition-all animate-in fade-in slide-in-from-top-1 duration-300"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(239,68,68,0.08) 0%, rgba(239,68,68,0.03) 100%)",
                    border: "1px solid rgba(239,68,68,0.18)",
                  }}
                >
                  <AlertTriangle
                    size={13}
                    className="shrink-0 mt-0.5"
                    style={{ color: C.red }}
                  />
                  <div>
                    <div
                      className="text-[10px] font-semibold"
                      style={{ color: C.red }}
                    >
                      {t("aiTradingPage.highRiskTitle")}
                    </div>
                    <div
                      className="text-[9px] leading-relaxed mt-0.5"
                      style={{ color: "rgba(239,68,68,0.7)" }}
                    >
                      {t("aiTradingPage.highRiskBody", {
                        threshold: "1,000",
                        tokenNameUsdt,
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Save button */}
          <button
            className="w-full py-3 rounded-xl font-bold text-sm btn-primary flex items-center justify-center gap-2"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <RefreshCw size={14} className="animate-spin" /> : null}
            {saving
              ? t("aiTradingPage.saving")
              : autoOpen
                ? t("aiTradingPage.saveAndEnable")
                : t("aiTradingPage.saveSettings")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════ */
export default function AITrading() {
  /* ─── State ─── */
  const { t } = useTranslation();
  const tokenNameUsdt = getTokenName(USDT_ADDRESS);
  const { address } = useAccount();
  const { open: openWalletModal } = useAppKit();
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [autoMargin, setAutoMargin] = useState(200);
  const [autoLeverage, setAutoLeverage] = useState(10);
  const [autoTradeDialogOpen, setAutoTradeDialogOpen] = useState(false);
  const { getTokenBalanceData } = useBalanceV2();
  const { data: cexPnlSummaryData } = useGetUserCexPnlSummary();
  const {
    data: platformCexSymbolDayStatsData,
    refetch: refetchPlatformCexSymbolDayStats,
  } = useGetPlatformCexSymbolDayStats();
  const usdtBalanceData = getTokenBalanceData(USDT_ADDRESS);
  const { data: platFormConfigData, refetch: refetchPlatFormConfig } =
    usePlatFormConfig();
  const availableUsdt = Number(
    formatEtherWithDecimals(usdtBalanceData?.canUseUsdtAmount ?? 0)
  );
  const todayPnl = Number(cexPnlSummaryData?.todayPnl ?? 0);
  const cumulativePnl = Number(cexPnlSummaryData?.cumulativePnl ?? 0);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardCoin, setWizardCoin] = useState("BTC");

  /* ── MaxQuant 实时 K 线：每张币卡独立 1s/1m/5m；连接按需懒创建且创建后常驻（避免反复切换时重复建连/加载） */
  const [klineByCoin, setKlineByCoin] = useState<
    Record<string, MaxQuantInterval>
  >(() => {
    const o: Record<string, MaxQuantInterval> = {};
    for (const c of SIGNAL_COINS) o[c] = "1m";
    return o;
  });
  const [klineLinksActivated, setKlineLinksActivated] = useState<
    Record<MaxQuantInterval, boolean>
  >({
    "1m": true,
    "1s": false,
    "5m": false,
  });

  const kline1m = useMaxQuantKline("1m", {
    enabled: klineLinksActivated["1m"],
  });
  const kline1s = useMaxQuantKline("1s", {
    enabled: klineLinksActivated["1s"],
  });
  const kline5m = useMaxQuantKline("5m", {
    enabled: klineLinksActivated["5m"],
  });

  const handleCoinIntervalChange = useCallback(
    (coin: string, nextInterval: MaxQuantInterval) => {
      setKlineByCoin(prev => ({ ...prev, [coin]: nextInterval }));
      setKlineLinksActivated(prev =>
        prev[nextInterval] ? prev : { ...prev, [nextInterval]: true }
      );
    },
    []
  );

  const klineReconnect = useCallback(() => {
    kline1m.reconnect();
    kline1s.reconnect();
    kline5m.reconnect();
  }, [kline1m.reconnect, kline1s.reconnect, kline5m.reconnect]);

  /** AiTrade：币卡统计（来源 `/game/cex/GetPlatformCexSymbolDayStats`） */
  const coinStats = useMemo(() => {
    const o: Record<string, { todayVolume: number; todayPnl: number }> = {};
    const statMap = new Map(
      (platformCexSymbolDayStatsData?.items ?? []).map(item => [
        item.symbol.toUpperCase(),
        item,
      ])
    );
    for (const coin of SIGNAL_COINS) {
      const s = statMap.get(`${coin}USDT`);
      o[coin] = {
        todayVolume: Number(s?.todayTradingVolume ?? 0),
        todayPnl: Number(s?.todayPnl ?? 0),
      };
    }
    return o;
  }, [platformCexSymbolDayStatsData]);

  const [pnlRefreshKey, setPnlRefreshKey] = useState(0);
  const activeOrderCount = MOCK_ORDERS.length;
  const canPlaceOrder =
    activeOrderCount < AI_TRADING_CONFIG.maxConcurrentOrders;

  const handleOpenWizard = async (coin: string) => {
    // 进入策略流程前：必须已连接钱包且存在登录 token
    if (!address || !getToken()) {
      openWalletModal();
      return;
    }
    await refetchPlatFormConfig();
    if (!platFormConfigData?.placeOrderSwitch) {
      showError(t("common.comingSoon"));
      return;
    }
    setWizardCoin(coin);
    setWizardOpen(true);
  };

  /* ─── Coin card prices ─── */
  // const coinPrices = useMemo<
  //   Record<string, { price: number; change: number }>
  // >(() => {
  //   const readFrom1s = (coin: string) => {
  //     const symbol = `${coin}USDT`;
  //     const snap = kline1s.snapshots[symbol];
  //     const points = snap?.points ?? [];
  //     const latest = points[points.length - 1]?.p ?? 0;
  //     const prev = points[points.length - 2]?.p ?? latest;
  //     const inferredChange = prev > 0 ? ((latest - prev) / prev) * 100 : 0;
  //     const changePct =
  //       typeof snap?.chg_in_min === "number"
  //         ? snap.chg_in_min * 100
  //         : inferredChange;
  //     return {
  //       price: latest > 0 ? latest : 0,
  //       change: Number.isFinite(changePct) ? changePct : 0,
  //     };
  //   };
  //
  //   return {
  //     BTC: readFrom1s("BTC"),
  //     ETH: readFrom1s("ETH"),
  //     BNB: readFrom1s("BNB"),
  //     SOL: readFrom1s("SOL"),
  //   };
  // }, [kline1s.snapshots]);
  const coinPrices = useMemo<Record<string, { price: number; change: number }>>(
    () => ({
      BTC: { price: 0, change: 0 },
      ETH: { price: 0, change: 0 },
      BNB: { price: 0, change: 0 },
      SOL: { price: 0, change: 0 },
    }),
    []
  );

  const handleRefresh = useCallback(async () => {
    await refetchPlatformCexSymbolDayStats();
    await new Promise(r => setTimeout(r, 1200));
    setPnlRefreshKey(k => k + 1);
    toast.success(t("aiTradingPage.dataUpdated"));
  }, [refetchPlatformCexSymbolDayStats, t]);

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div
        className="min-h-screen pb-24 pt-[80px]"
        style={{ background: "rgba(10,11,14,0.55)" }}
      >
        {/* 与 AiTrade `<main className="… max-w-7xl … px-4 sm:px-6 …">` 一致：主内容区 + 子区块 space-y-5 / sm:space-y-8 */}
        <div className="w-full  mx-auto px-4 sm:px-6 space-y-5 sm:space-y-8">
          {/* ═══ ASSETS SUMMARY CARD ═══ */}
          <div className="pt-2">
            <div
              className="rounded-xl overflow-hidden relative"
              style={{ background: C.card, border: `1px solid ${C.border}` }}
            >
              {/* Subtle gradient accent */}
              <div
                className="absolute top-0 left-0 right-0 h-[2px]"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(34,197,94,0.5) 0%, rgba(34,197,94,0.1) 60%, transparent 100%)",
                }}
              />

              <div className="p-4">
                {/* Row 1: Label + Action */}
                <div className="flex items-center justify-between mb-1">
                  <span
                    className="text-[11px] tracking-wide"
                    style={{ color: C.dim }}
                  >
                    {t("aiTradingPage.availableAssets", { tokenNameUsdt })}
                  </span>
                  {/* <button
                    onClick={() => navigate("/performance")}
                    className="flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-lg transition-all active:scale-95"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(34,197,94,0.12) 0%, rgba(34,197,94,0.04) 100%)",
                      WebkitBackdropFilter: "blur(8px) saturate(1.3)",
                      backdropFilter: "blur(8px) saturate(1.3)",
                      border: "1px solid rgba(34,197,94,0.22)",
                      color: C.green,
                      boxShadow:
                        "0 0 8px rgba(34,197,94,0.08), inset 0 1px 0 rgba(255,255,255,0.06)",
                    }}
                  >
                    <BarChart2 size={10} />
                    分析表现
                  </button> */}
                </div>

                {/* Row 2: Big balance + Today PnL inline */}
                <AnimatedPnlRow
                  balanceUsdt={availableUsdt}
                  todayPnl={todayPnl}
                  totalPnl={cumulativePnl}
                  refreshKey={pnlRefreshKey}
                />
              </div>
            </div>
          </div>

          {/* ===== Section 2: 自动 AI + 币卡（配色/字号/布局对齐 forttrade-dapp AITrading 2611-2740） ===== */}
          <section>
            {/* 自动 AI 下单条 */}
            {/* <div className="pb-3">
              <button
                type="button"
                onClick={() => setAutoTradeDialogOpen(true)}
                className="w-full rounded-xl p-3.5 text-left transition-all relative overflow-hidden"
                style={{
                  background: autoEnabled
                    ? "linear-gradient(135deg, rgba(34,197,94,0.08) 0%, rgba(15,17,23,0.85) 60%)"
                    : "linear-gradient(180deg, rgba(34,197,94,0.04) 0%, rgba(15,17,23,0.6) 100%)",
                  WebkitBackdropFilter: "blur(12px) saturate(1.3)",
                  backdropFilter: "blur(12px) saturate(1.3)",
                  border: `1px solid ${
                    autoEnabled
                      ? "rgba(34,197,94,0.30)"
                      : "rgba(34,197,94,0.12)"
                  }`,
                  boxShadow: autoEnabled
                    ? "0 0 24px rgba(34,197,94,0.10), inset 0 1px 0 rgba(34,197,94,0.12), inset 0 1px 0 rgba(255,255,255,0.05)"
                    : "inset 0 1px 0 rgba(255,255,255,0.04)",
                }}
              >
                {autoEnabled && (
                  <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl">
                    <div
                      className="absolute top-0 left-0 w-full h-full"
                      style={{
                        background:
                          "linear-gradient(90deg, transparent 0%, rgba(34,197,94,0.06) 50%, transparent 100%)",
                        animation: "autoScanLine 3s ease-in-out infinite",
                      }}
                    />
                  </div>
                )}
                <div className="flex items-center justify-between relative z-10">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      {autoEnabled && (
                        <div
                          className="absolute inset-0 rounded-lg"
                          style={{
                            border: "1.5px solid rgba(34,197,94,0.40)",
                            animation: "autoPulseRing 2s ease-out infinite",
                          }}
                        />
                      )}
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center relative"
                        style={{
                          background: autoEnabled
                            ? "rgba(34,197,94,0.15)"
                            : "rgba(6,182,212,0.08)",
                          border: `1px solid ${
                            autoEnabled
                              ? "rgba(34,197,94,0.30)"
                              : "rgba(6,182,212,0.20)"
                          }`,
                        }}
                      >
                        <Zap
                          size={18}
                          style={{
                            color: autoEnabled ? C.primary : C.cyan,
                            filter: autoEnabled
                              ? "drop-shadow(0 0 4px rgba(34,197,94,0.5))"
                              : "none",
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <div
                        className="text-sm font-bold"
                        style={{ color: C.text }}
                      >
                        {autoEnabled ? "AI 自动下单运行中" : "自动 AI 下单"}
                      </div>
                      <div
                        className="text-[10px]"
                        style={{ color: autoEnabled ? C.muted : C.dim }}
                      >
                        {autoEnabled
                          ? `$${autoMargin} · 今日 ${AI_TRADING_CONFIG.autoTrade.todayOrderCount}/${AI_TRADING_CONFIG.autoTrade.dailyOrderLimit} 单`
                          : "订阅后 AI 将自动分析市场并执行交易"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {autoEnabled && (
                      <span
                        className="text-[9px] px-2 py-0.5 rounded-full font-semibold flex items-center gap-1"
                        style={{
                          background:
                            "linear-gradient(180deg, rgba(34,197,94,0.18) 0%, rgba(34,197,94,0.08) 100%)",
                          WebkitBackdropFilter: "blur(8px)",
                          backdropFilter: "blur(8px)",
                          color: C.green,
                          border: "1px solid rgba(34,197,94,0.30)",
                          boxShadow:
                            "0 0 8px rgba(34,197,94,0.12), inset 0 1px 0 rgba(255,255,255,0.06)",
                        }}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full inline-block"
                          style={{
                            background: C.green,
                            animation: "autoDotBlink 1.5s ease-in-out infinite",
                          }}
                        />
                        运行中
                      </span>
                    )}
                    <ChevronRight
                      size={16}
                      style={{ color: autoEnabled ? C.muted : C.dim }}
                    />
                  </div>
                </div>
              </button>
            </div> */}

            <div className="pb-4">
              <div
                className="flex gap-3 overflow-x-auto pb-2"
                style={{ scrollSnapType: "x mandatory" }}
              >
                {SIGNAL_COINS.map(coin => {
                  const pair = TRADING_PAIRS.find(p => p.id === coin);
                  const logo = COIN_LOGOS[coin];
                  const vol = coinStats?.[coin]?.todayVolume;
                  const pnl = coinStats?.[coin]?.todayPnl ?? 0;
                  const isPnlPositive = pnl >= 0;
                  if (!pair) return null;
                  return (
                    <div
                      key={coin}
                      className="shrink-0 rounded-xl overflow-hidden"
                      style={{
                        width: "280px",
                        scrollSnapAlign: "start",
                        background: C.card,
                        border: `1px solid ${C.border}`,
                      }}
                    >
                      <div className="px-3 pt-2.5 pb-1.5">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-7 h-7 rounded-full flex items-center justify-center overflow-hidden"
                              style={{
                                background: pair.bgColor,
                                border: `1px solid ${pair.borderColor}`,
                              }}
                            >
                              {logo ? (
                                <img
                                  src={logo}
                                  alt={coin}
                                  className="w-5 h-5 object-contain"
                                />
                              ) : (
                                <span
                                  className="text-[10px] font-bold"
                                  style={{ color: pair.color }}
                                >
                                  {coin.charAt(0)}
                                </span>
                              )}
                            </div>
                            <div>
                              <div
                                className="text-sm font-bold"
                                style={{ color: C.text }}
                              >
                                {pair.symbol}
                              </div>
                              <div
                                className="text-[9px]"
                                style={{ color: C.dim }}
                              >
                                {pair.name}
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleOpenWizard(coin)}
                            className="px-2.5 py-1.5 rounded-lg text-[10px] font-bold flex items-center gap-1 transition-all active:scale-[0.98]"
                            style={{
                              background:
                                "linear-gradient(180deg, rgba(34,197,94,0.14) 0%, rgba(34,197,94,0.06) 100%)",
                              WebkitBackdropFilter: "blur(8px) saturate(1.3)",
                              backdropFilter: "blur(8px) saturate(1.3)",
                              border: "1px solid rgba(34,197,94,0.30)",
                              color: C.green,
                              boxShadow:
                                "0 0 10px rgba(34,197,94,0.10), inset 0 1px 0 rgba(255,255,255,0.08)",
                              textShadow: "0 0 6px rgba(34,197,94,0.4)",
                            }}
                          >
                            <Zap size={9} />
                            {t("aiTradingPage.getAiStrategy")}
                          </button>
                        </div>

                        <div className="flex items-baseline justify-between">
                          <div>
                            <div
                              className="text-sm font-bold mono-num"
                              style={{ color: C.green }}
                            >
                              +
                              {vol != null
                                ? vol.toLocaleString("en-US", {
                                    maximumFractionDigits: 0,
                                  })
                                : "0"}
                            </div>
                            <div
                              className="text-[8px]"
                              style={{ color: C.dim }}
                            >
                              {t("aiTradingPage.todayVolume")}
                            </div>
                          </div>
                          <div className="text-right">
                            <div
                              className="text-xs font-bold mono-num"
                              style={{ color: isPnlPositive ? C.green : C.red }}
                            >
                              {isPnlPositive ? "+" : ""}
                              {pnl.toFixed(2)}
                            </div>
                            <div
                              className="text-[8px]"
                              style={{ color: C.dim }}
                            >
                              {t("aiTradingPage.todayPnlLabel")}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="px-1 pb-1">
                        {(() => {
                          const sym = `${coin}USDT`;
                          const intv = klineByCoin[coin] ?? "1m";
                          const pool =
                            intv === "1m"
                              ? kline1m
                              : intv === "1s"
                                ? kline1s
                                : kline5m;
                          const snapK = pool.snapshots[sym] ?? null;
                          const hasKline = (snapK?.points?.length ?? 0) > 0;
                          const klineLoading =
                            !hasKline && !pool.awaitSnapshotTimedOut;
                          const klineErr =
                            !hasKline && pool.awaitSnapshotTimedOut
                              ? t("aiTradingPage.klineLoadTimeout")
                              : null;
                          return (
                            <SignalChart
                              snapshot={snapK}
                              symbol={sym}
                              interval={intv}
                              onIntervalChange={v =>
                                handleCoinIntervalChange(
                                  coin,
                                  v as MaxQuantInterval
                                )
                              }
                              loading={klineLoading}
                              loadError={klineErr}
                              onLoadRetry={klineReconnect}
                            />
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </div>

        <OrdersHistorySection
          coinPrices={coinPrices}
          onReorderFromHistory={handleOpenWizard}
        />

        {/* ═══ Dialogs ═══ */}
        <StrategyWizardDialog
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          canPlaceOrder={canPlaceOrder}
          activeOrderCount={activeOrderCount}
          selectedCoinFromCard={wizardCoin}
        />
        <AutoTradeDialog
          open={autoTradeDialogOpen}
          onClose={() => setAutoTradeDialogOpen(false)}
          autoEnabled={autoEnabled}
          setAutoEnabled={setAutoEnabled}
          margin={autoMargin}
          setMargin={setAutoMargin}
          leverage={autoLeverage}
          setLeverage={setAutoLeverage}
        />
      </div>
    </PullToRefresh>
  );
}
